import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  getEnterprises,
  getContractTypes,
  getOrganizations,
  getProducts,
  saveOrganization,
} from '../../lib/api';
import { Table, TableCell } from '../../components/table/Table';
import OrganizationDetails from './OrganizationDetails';

const newOrganization = {
  name: '',
  isActive: true,
};

export default function OrganizationManagement() {
  const [organizations, setOrganizations] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getOrganizations().then((all) => {
      setOrganizations(all);
      setIsLoading(false);
    });

    getEnterprises().then((all) => {
      setEnterprises(all);
    });

    getContractTypes().then((all) => {
      setContractTypes(all);
    });

    getProducts().then((all) => {
      setProducts(all.map((p) => ({ ...p, name: p.label })));
    });
  }, []);

  return (
    <Box
      sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {buildHeader()}
      {buildTable()}
      {buildNewOrganizationDialog()}
    </Box>
  );

  function buildHeader() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ display: 'inline-block', flex: 1 }} variant="h5">
          Organization Management
        </Typography>
        <Button
          onClick={() => setCurrentOrganization(newOrganization)}
          variant="contained"
          color="primary"
        >
          Add New Organization
        </Button>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table
        disableVirtualization
        loading={isLoading}
        hideFooter={false}
        rows={organizations}
        columns={buildTableColumns()}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          sorting: {
            sortModel: [{ field: 'name', sort: 'asc' }],
          },
        }}
        localeText={{
          footerTotalRows: 'Total Organizations:',
        }}
      />
    );
  }

  function buildTableColumns() {
    return [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1.25,
      },
      {
        field: 'enterprise',
        headerName: 'Enterprise',
        flex: 1.25,
        valueGetter: ({ row }) => row.enterprise?.name,
      },
      {
        field: 'id',
        headerName: 'GUID',
        flex: 1.25,
        renderCell: (params) => (
          <TableCell
            copyTarget="GUID"
            copyOnClick={params.row.id}
            renderCellParams={params}
          />
        ),
      },
      {
        field: 'contractTypes',
        headerName: 'Active Contracts',
        flex: 1.25,
        valueGetter: ({ row }) =>
          row.contractTypes
            ?.map((contractType) => contractType.name)
            .join(', '),
      },
      {
        field: 'isActive',
        headerName: 'Active',
        valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
      },
      {
        field: 'edit',
        headerName: 'Edit',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            onClick={() => setCurrentOrganization(row)}
            variant="outlined"
            color="inherit"
            data-testid={`edit ${row.name}`}
            disabled={row.name === 'Advanced Agrilytics'}
          >
            Edit
          </Button>
        ),
      },
    ].filter(Boolean);
  }

  function save(organization) {
    setIsLoading(true);

    return saveOrganization(organization).then(() => {
      closeDialog();

      getOrganizations().then((all) => {
        setOrganizations(all);
        setIsLoading(false);
      });
    });
  }

  function closeDialog() {
    setCurrentOrganization(null);
  }

  function buildNewOrganizationDialog() {
    if (!currentOrganization) return null;

    return (
      <OrganizationDetails
        organization={currentOrganization}
        enterprises={enterprises}
        contractTypes={contractTypes}
        products={products}
        handleSave={save}
        handleClose={closeDialog}
      />
    );
  }
}
